/*
  LASE — Parts catalogue faceted filtering.

  The section (lase-parts-catalog.liquid) renders one card per variant
  with data-finish / data-pattern / data-size / data-color (+ data-hex
  for the colour swatch dot). This script:

    1. Reads every card, derives the unique values present per facet.
    2. Builds the filter chips (only facets that have >1 value show).
    3. Filters client-side: a card is visible when, for EVERY facet that
       has a selection, the card's value is among the selected ones
       (AND across facets, OR within a facet). Nothing is hidden until
       the customer picks a facet — full catalogue by default.

  No URL round-trip; pure DOM toggling. Re-runs on
  shopify:section:load so the theme editor stays live.
*/
(function () {
  'use strict';

  // Facet definitions in display order. `pretty` formats the chip
  // label; `order` pins chip order (else first-seen); `swatch` draws a
  // colour dot from the card's data-hex.
  const DEFAULT_FACETS = [
    { key: 'type',    label: 'Type',    order: ['Module', 'Connector', 'Glass'] },
    { key: 'finish',  label: 'Finish',  capitalize: true },
    { key: 'pattern', label: 'Pattern' },
    { key: 'size',    label: 'Size',    order: ['S', 'M', 'L', 'XL'] },
    { key: 'color',   label: 'Colour',  swatch: true }
  ];
  // A section may override facets via data-facets='[{"key","label",...}]'.
  // Supported flags: order (array), swatch (bool), capitalize (bool),
  // multi (bool — card holds comma-separated values, matches on ANY).
  function facetsFor (section) {
    try {
      const raw = section.dataset.facets;
      if (raw) return JSON.parse(raw);
    } catch (e) { /* fall through */ }
    return DEFAULT_FACETS;
  }
  function cardValues (card, f) {
    const raw = card.dataset[f.key] || '';
    if (!raw) return [];
    return f.multi ? raw.split(',').map(v => v.trim()).filter(Boolean) : [raw];
  }

  function capitalize (s) {
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
  }

  function setup (section) {
    if (section.dataset.lasePartsBound) return;
    section.dataset.lasePartsBound = '1';

    const grid     = section.querySelector('[data-lase-parts-grid]');
    const filters  = section.querySelector('[data-lase-parts-filters]');
    const countEl  = section.querySelector('[data-lase-parts-count]');
    const clearBtn = section.querySelector('[data-lase-parts-clear]');
    const emptyEl  = section.querySelector('[data-lase-parts-empty]');
    if (!grid || !filters) return;

    const cards = Array.from(grid.querySelectorAll('.lase-parts__item'));
    if (!cards.length) return;

    const FACETS = facetsFor(section);

    // Count label noun — sections override via data-count-noun='["product","products"]'.
    let NOUN = ['part', 'parts'];
    try {
      const rawNoun = JSON.parse(section.dataset.countNoun || 'null');
      if (Array.isArray(rawNoun) && rawNoun.length === 2) NOUN = rawNoun;
    } catch (e) { /* keep default */ }

    // Selected values per facet.
    const state = {};
    FACETS.forEach(f => { state[f.key] = new Set(); });

    // Derive unique values (+ a colour→hex map) from the cards.
    const values = {};
    let hexByColor = {};
    try { hexByColor = JSON.parse(grid.dataset.hexmap || '{}'); } catch (e) {}
    FACETS.forEach(f => { values[f.key] = []; });
    cards.forEach(card => {
      FACETS.forEach(f => {
        cardValues(card, f).forEach(v => {
          if (values[f.key].indexOf(v) === -1) values[f.key].push(v);
        });
      });
      if (card.dataset.color && card.dataset.hex && !hexByColor[card.dataset.color]) {
        hexByColor[card.dataset.color] = card.dataset.hex;
      }
    });

    // Build chips for facets that actually discriminate (>1 value).
    FACETS.forEach(f => {
      let vals = values[f.key];
      if (vals.length < 2) return;
      if (f.order) {
        vals = vals.slice().sort((a, b) => {
          const ia = f.order.indexOf(a), ib = f.order.indexOf(b);
          return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
        });
      }
      const group = document.createElement('div');
      group.className = 'lase-parts__facet';
      const lbl = document.createElement('span');
      lbl.className = 'lase-parts__facet-label';
      lbl.textContent = f.label;
      group.appendChild(lbl);

      const chips = document.createElement('div');
      chips.className = 'lase-parts__chips';
      vals.forEach(v => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'lase-parts__chip';
        chip.dataset.facet = f.key;
        chip.dataset.value = v;
        chip.setAttribute('aria-pressed', 'false');
        if (f.swatch && hexByColor[v]) {
          const dot = document.createElement('span');
          dot.className = 'lase-parts__chip-dot';
          dot.style.background = hexByColor[v];
          chip.appendChild(dot);
        }
        const txt = document.createElement('span');
        txt.textContent = f.pretty ? f.pretty(v) : (f.capitalize ? capitalize(v) : v);
        chip.appendChild(txt);
        chip.addEventListener('click', () => {
          const set = state[f.key];
          if (set.has(v)) set.delete(v); else set.add(v);
          chip.classList.toggle('is-active', set.has(v));
          chip.setAttribute('aria-pressed', set.has(v) ? 'true' : 'false');
          apply();
        });
        chips.appendChild(chip);
      });
      group.appendChild(chips);
      filters.appendChild(group);
    });

    if (filters.children.length) filters.hidden = false;

    function apply () {
      let visible = 0;
      cards.forEach(card => {
        const show = FACETS.every(f => {
          const set = state[f.key];
          if (set.size === 0) return true;
          return cardValues(card, f).some(v => set.has(v));
        });
        card.hidden = !show;
        if (show) visible++;
      });
      if (countEl) {
        countEl.textContent = visible + ' ' + (visible === 1 ? NOUN[0] : NOUN[1]);
      }
      if (emptyEl) emptyEl.hidden = visible !== 0;
      const anyActive = FACETS.some(f => state[f.key].size > 0);
      if (clearBtn) clearBtn.hidden = !anyActive;
    }

    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        FACETS.forEach(f => state[f.key].clear());
        filters.querySelectorAll('.lase-parts__chip.is-active').forEach(c => {
          c.classList.remove('is-active');
          c.setAttribute('aria-pressed', 'false');
        });
        apply();
      });
    }

    apply();
  }

  function init () {
    document.querySelectorAll('[data-lase-parts]').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
